/**
 * ConversionRedirectsPanel.js — "Payments" tab
 * Manage the payment buttons shown on the public WordPress /payments page.
 * OmniLeagues is the source of truth: each {label, url, enabled} row is
 * projected to WP and rendered by the [ol_pay_button] shortcode. Only
 * ENABLED buttons that also have a URL appear on the page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConversionRedirectsPanel({ orgId }) {
  const [payButtons, setPayButtons] = useState([]);
  const [paySaving, setPaySaving] = useState(false);
  const [payResult, setPayResult] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/website-settings/${orgId}`, { withCredentials: true })
      .then(({ data }) => setPayButtons(Array.isArray(data.pay_buttons) ? data.pay_buttons : []))
      .catch(() => {});
  }, [orgId]);

  const setBtn = (idx, key, val) =>
    setPayButtons((list) => list.map((b, i) => (i === idx ? { ...b, [key]: val } : b)));

  const addBtn = () =>
    setPayButtons((list) => [
      ...list,
      { id: `new-${Date.now()}`, label: '', url: '', enabled: true },
    ]);

  const removeBtn = (idx) =>
    setPayButtons((list) => list.filter((_, i) => i !== idx));

  const savePay = async () => {
    setPaySaving(true);
    setPayResult(null);
    try {
      const { data } = await axios.patch(
        `${API}/website-settings/${orgId}`,
        { pay_buttons: payButtons },
        { withCredentials: true },
      );
      setPayButtons(Array.isArray(data.pay_buttons) ? data.pay_buttons : []);
      setPayResult({ ok: true, count: (data.pay_buttons || []).filter((b) => b.enabled && b.url).length });
    } catch (e) {
      setPayResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setPaySaving(false);
    }
  };

  const liveCount = payButtons.filter((b) => b.enabled && (b.url || '').trim()).length;

  return (
    <div data-testid="conversion-redirects-panel" className="p-6 space-y-6 text-gray-900 dark:text-gray-100">
      <div>
        <h2 className="text-lg font-semibold">Payments</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage the payment buttons on your public <code>/payments</code> page. Each enabled button
          with a URL appears on the page. OmniLeagues is the source of truth — changes publish to the
          live site automatically (within about a minute).
        </p>
      </div>

      <div className="border rounded p-4 dark:border-slate-700 dark:bg-slate-900/40" data-testid="pay-buttons-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-medium">Payment Buttons</h3>
          <button
            data-testid="pay-button-add"
            onClick={addBtn}
            className="px-3 py-1.5 bg-gray-100 border rounded text-sm hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600 dark:hover:bg-slate-600"
          >+ Add Button</button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Each enabled button appears horizontally on the Payments page. Give each a label and its own
          payment URL. Toggle off to hide a button without deleting it. <strong>An enabled button with
          no URL will not appear on the site.</strong>
        </p>

        <div className="space-y-3">
          {payButtons.length === 0 && (
            <div className="text-sm text-gray-400 dark:text-gray-500" data-testid="pay-buttons-empty">
              No buttons yet — click &ldquo;+ Add Button&rdquo;.
            </div>
          )}
          {payButtons.map((b, idx) => {
            const missingUrl = b.enabled && !(b.url || '').trim();
            return (
            <div
              key={b.id || idx}
              data-testid={`pay-button-row-${idx}`}
              className="border rounded p-3 bg-gray-50 dark:bg-slate-800/60 dark:border-slate-700"
            >
              <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-3">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Label</label>
                <input
                  data-testid={`pay-button-label-${idx}`}
                  value={b.label || ''}
                  onChange={(e) => setBtn(idx, 'label', e.target.value)}
                  placeholder="e.g. Sponsorship"
                  className="w-full border rounded px-3 py-2 text-sm bg-white text-gray-900 placeholder:text-gray-400 dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600 dark:placeholder:text-gray-500"
                />
              </div>
              <div className="col-span-6">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Payment URL</label>
                <input
                  data-testid={`pay-button-url-${idx}`}
                  value={b.url || ''}
                  onChange={(e) => setBtn(idx, 'url', e.target.value)}
                  placeholder="https://square.link/u/..."
                  className={`w-full border rounded px-3 py-2 text-sm bg-white text-gray-900 placeholder:text-gray-400 dark:bg-slate-800 dark:text-gray-100 dark:placeholder:text-gray-500 ${missingUrl ? 'border-amber-400 dark:border-amber-500' : 'dark:border-slate-600'}`}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Show on page</label>
                <button
                  type="button"
                  data-testid={`pay-button-toggle-${idx}`}
                  onClick={() => setBtn(idx, 'enabled', !b.enabled)}
                  className={`w-full px-3 py-2 rounded text-sm border ${
                    b.enabled
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-600 border-gray-300 dark:bg-slate-700 dark:text-gray-300 dark:border-slate-600'
                  }`}
                >{b.enabled ? 'Enabled' : 'Disabled'}</button>
              </div>
              <div className="col-span-1">
                <button
                  type="button"
                  data-testid={`pay-button-remove-${idx}`}
                  onClick={() => removeBtn(idx)}
                  title="Remove button"
                  className="w-full px-2 py-2 rounded text-sm border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/60 dark:hover:bg-red-900/20"
                >✕</button>
              </div>
              </div>
              {missingUrl && (
                <div data-testid={`pay-button-warn-${idx}`} className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  ⚠ This button is enabled but has no URL, so it will not show on the site. Add a payment URL or toggle it off.
                </div>
              )}
            </div>
          );})}
        </div>

        <div className="flex items-center justify-end mt-4">
          <button
            data-testid="pay-buttons-save"
            onClick={savePay}
            disabled={paySaving}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >{paySaving ? 'Saving…' : 'Save Payment Buttons'}</button>
        </div>
        {payResult && (
          <div
            data-testid="pay-buttons-result"
            className={`mt-3 p-2 rounded text-xs ${payResult.ok ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}
          >
            {payResult.ok ? `✓ Saved. ${payResult.count} button(s) will show on the Payments page.` : `✗ ${payResult.error}`}
          </div>
        )}
        {!payResult && liveCount === 0 && payButtons.length > 0 && (
          <div className="mt-3 text-xs text-amber-600 dark:text-amber-400" data-testid="pay-buttons-none-live">
            No buttons will currently show on the site — enable at least one and give it a URL.
          </div>
        )}
      </div>
    </div>
  );
}
